import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Float, Sphere, Box, Torus } from "@react-three/drei";
import { Link } from "react-router-dom";
import PageSeo from "../components/PageSeo";
import Particles from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { useApp } from "../context/AppContext";
const featuredProducts = [
  {
    id: 1,
    name: "Insta360 X3 Action Camera",
    price: "Rs 29,990",
    category: "Electronics",
    image: "https://m.media-amazon.com/images/I/610NQUzk2xL._SX679_.jpg",
  },
  {
    id: 2,
    name: "Tubeless Tyre Puncture Repair Kit",
    price: "Rs 298",
    category: "Maintenance",
    image: "https://m.media-amazon.com/images/I/61iuZ0CYSrL._AC_SR180,120_CB1169409_QL70_.jpg",
  },
  {
    id: 3,
    name: "Full Face Riding Helmet",
    price: "Rs 2,499",
    category: "Riding Gear",
    image: "https://m.media-amazon.com/images/I/61fzUXgm6hL._AC_UL320_.jpg",
  },
];

function Bike3D() {
  return (
    <group>
      {/* Main bike body */}
      <Float speed={2} rotationIntensity={1} floatIntensity={0.5}>
        <Box args={[2, 0.5, 4]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#ff7a00" metalness={0.8} roughness={0.2} />
        </Box>
      </Float>

      {/* Wheels */}
      <Float speed={3} rotationIntensity={2}>
        <Torus args={[0.8, 0.2, 8, 16]} position={[-1, -0.5, 1.5]} rotation={[Math.PI / 2, 0, 0]}>
          <meshStandardMaterial color="#333" />
        </Torus>
        <Torus args={[0.8, 0.2, 8, 16]} position={[1, -0.5, 1.5]} rotation={[Math.PI / 2, 0, 0]}>
          <meshStandardMaterial color="#333" />
        </Torus>
      </Float>

      {/* Handlebars */}
      <Box args={[0.1, 1, 0.1]} position={[0, 0.8, -1.5]}>
        <meshStandardMaterial color="#666" />
      </Box>

      {/* Engine */}
      <Sphere args={[0.4]} position={[0, -0.2, 0.5]}>
        <meshStandardMaterial color="#222" metalness={0.9} roughness={0.1} />
      </Sphere>

      {/* Exhaust */}
      <Torus args={[0.15, 0.05, 8, 16]} position={[-0.5, -0.3, 1]} rotation={[0, Math.PI / 2, 0]}>
        <meshStandardMaterial color="#444" />
      </Torus>
    </group>
  );
}

function FloatingElements() {
  return (
    <>
      {/* Floating geometric shapes */}
      <Float speed={1.5} rotationIntensity={1} floatIntensity={1}>
        <Sphere args={[0.3]} position={[-3, 2, -2]}>
          <meshStandardMaterial color="#ff4d00" emissive="#ff4d00" emissiveIntensity={0.2} />
        </Sphere>
      </Float>

      <Float speed={2} rotationIntensity={1.5} floatIntensity={0.8}>
        <Box args={[0.4, 0.4, 0.4]} position={[3, -1, -3]}>
          <meshStandardMaterial color="#ff9900" emissive="#ff9900" emissiveIntensity={0.15} />
        </Box>
      </Float>

      <Float speed={1} rotationIntensity={2} floatIntensity={1.2}>
        <Torus args={[0.5, 0.1, 8, 16]} position={[-2, -2, 2]} rotation={[Math.PI / 4, 0, 0]}>
          <meshStandardMaterial color="#ff7a00" emissive="#ff7a00" emissiveIntensity={0.1} />
        </Torus>
      </Float>
    </>
  );
}

export default function Home() {
  const { user, rides = [] } = useApp() || {};
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 900 : false));
  const firstName = user?.name?.trim()?.split(/\s+/)?.[0] || "Rider";
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const particlesOptions = useMemo(() => ({
    background: {
      color: {
        value: "transparent",
      },
    },
    fpsLimit: 120,
    interactivity: {
      events: {
        onClick: {
          enable: true,
          mode: "push",
        },
        onHover: {
          enable: true,
          mode: "repulse",
        },
        resize: true,
      },
      modes: {
        push: {
          quantity: 4,
        },
        repulse: {
          distance: 200,
          duration: 0.4,
        },
      },
    },
    particles: {
      color: {
        value: ["#ff4d00", "#ff7a00", "#ff9900", "#ffb366"],
      },
      links: {
        color: "#ff7a00",
        distance: 150,
        enable: true,
        opacity: 0.3,
        width: 1,
      },
      collisions: {
        enable: true,
      },
      move: {
        direction: "none",
        enable: true,
        outModes: {
          default: "bounce",
        },
        random: false,
        speed: 1,
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 800,
        },
        value: 80,
      },
      opacity: {
        value: 0.5,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 1, max: 5 },
      },
    },
    detectRetina: true,
  }), []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const recommendedRides = useMemo(() => {
    if (!user?.city) return rides.slice(0, 3);

    const normalizedCity = user.city.trim().toLowerCase();
    const scoredRides = rides.map((ride) => {
      let score = 0;
      if ((ride.from || "").toLowerCase() === normalizedCity) score += 4;
      if ((ride.to || "").toLowerCase() === normalizedCity) score += 2;
      if ((ride.from || "").toLowerCase().includes(normalizedCity)) score += 1;
      if ((ride.to || "").toLowerCase().includes(normalizedCity)) score += 1;
      return { ride, score };
    });

    return scoredRides
      .sort((a, b) => b.score - a.score)
      .map((item) => item.ride)
      .slice(0, 3);
  }, [rides, user]);

  const profileCompletion = useMemo(() => {
    if (!user) return 0;
    const checks = [
      Boolean(user.name),
      Boolean(user.city),
      Boolean(user.bike),
      Boolean(user.phone || user.email),
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [user]);

  const recentTools = [
    { title: "EMI Calculator", path: "/calculator/emi", desc: "Plan your monthly bike budget" },
    { title: "Fuel Calculator", path: "/calculator/fuel", desc: "Estimate ride fuel spend fast" },
    { title: "Compare Bikes", path: "/compare", desc: "Check side-by-side bike options" },
  ];

  const unlockedFeatures = [
    { title: "Find Rides", path: "/rides", desc: "Join nearby rides and weekend plans" },
    { title: "Open Chat", path: "/chat", desc: "Talk with riders and chapters instantly" },
    { title: "Emergency SOS", path: "/sos", desc: "Reach help quickly when you need it" },
  ];

  const communityHighlights = [
    {
      title: "Rider Profiles Across India",
      body: "Every rider can explore public profiles by city or state, follow riders, and connect with like-minded communities.",
      ctaLabel: "Explore Riders",
      ctaPath: "/riders",
    },
    {
      title: "Create Community Blogs",
      body: "Share ride stories, route tips, and bike reviews. Your post can inspire riders in your city and beyond.",
      ctaLabel: "Write a Blog",
      ctaPath: "/blog/write",
    },
    {
      title: "Find Nearby Riders Fast",
      body: "Use Near Me in Riders directory to discover active bikers around your current city for weekend plans.",
      ctaLabel: "Open Near Me",
      ctaPath: "/riders",
    },
    {
      title: "Plan + Compare + Ride",
      body: "Move from bike comparison to calculators and routes in one flow, so decisions are easier and smarter.",
      ctaLabel: "Start Comparing",
      ctaPath: "/compare",
    },
  ];

  return (
    <>
      <PageSeo
        title="Bike EMI Calculator, Fuel Cost & Trip Planner India | MotoHub India"
        description="MotoHub India helps riders compare bikes, calculate EMI and fuel costs, plan routes, and join a trusted biking community across India."
        keywords={[
          "bike EMI calculator India",
          "fuel cost calculator bike",
          "trip planner for bikers",
          "compare bikes India",
          "motorcycle community India",
          "rider profile India",
          "create bike blog India",
          "find nearby riders India",
          "MotoHub India"
        ]}
        path="/"
        image="/logo5.png"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "MotoHub India",
          url: "https://motohubindia.com/",
          description: "Bike EMI calculator, fuel cost planner, routes, and community tools for riders in India.",
          inLanguage: "en-IN",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://motohubindia.com/bikes?search={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />

      {/* Hero Section with Enhanced 3D */}
      <div style={{ position: "relative", height: isMobile ? "420px" : "500px", overflow: "hidden" }}>
        {/* Interactive Particles Background */}
        <Particles
          id="tsparticles"
          init={particlesInit}
          options={particlesOptions}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1
          }}
        />

        {/* 3D Canvas */}
        <Canvas
          style={{
            height: "100%",
            background: "radial-gradient(circle at center, #0a0a0a 0%, #000 100%)",
            zIndex: 2
          }}
          camera={{ position: [0, 0, 8], fov: 50 }}
        >
          {/* Enhanced Lighting */}
          <ambientLight intensity={0.3} />
          <directionalLight position={[5, 5, 5]} intensity={1} />
          <pointLight position={[-5, -5, 5]} intensity={0.5} color="#ff7a00" />
          <spotLight
            position={[0, 10, 0]}
            angle={0.3}
            penumbra={1}
            intensity={0.5}
            color="#ff4d00"
            castShadow
          />

          {/* 3D Elements */}
          <Bike3D />
          <FloatingElements />

          {/* Interactive Controls */}
          <OrbitControls
            enableZoom={true}
            enablePan={true}
            maxDistance={12}
            minDistance={4}
            autoRotate={true}
            autoRotateSpeed={0.5}
          />
        </Canvas>

        {/* Hero Content Overlay */}
        <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          color: "#fff",
          zIndex: 3,
          textShadow: "0 0 20px rgba(255, 122, 0, 0.5)"
        }}>
          <h1 style={{
            fontSize: isMobile ? "2.4rem" : "4rem",
            margin: 0,
            fontFamily: "var(--font-display)",
            background: "linear-gradient(45deg, #ff4d00, #ff9900, #ffb366)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            animation: "glow 2s ease-in-out infinite alternate"
          }}>
            🏍️ MotoHub India
          </h1>
          <p style={{
            fontSize: isMobile ? "1.1rem" : "1.5rem",
            margin: "15px 0",
            fontWeight: "300"
          }}>
            India’s Smart Platform for Bikers

          </p>
          <p style={{
            fontSize: isMobile ? "0.95rem" : "1.1rem",
            color: "#ccc",
            marginBottom: "30px"
          }}>
            Plan rides • Calculate costs • Connect with riders
          </p>
          <div style={{ display: "flex", gap: "15px", justifyContent: "center", flexWrap: "wrap" }}>
            <Link
              to="/recommend"
              style={{
                padding: "12px 25px",
                background: "linear-gradient(45deg, #ff4d00, #ff7a00)",
                color: "#fff",
                borderRadius: "25px",
                textDecoration: "none",
                fontWeight: "bold",
                boxShadow: "0 4px 15px rgba(255, 122, 0, 0.3)",
                transition: "all 0.3s ease",
                border: "2px solid transparent"
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 8px 25px rgba(255, 122, 0, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 15px rgba(255, 122, 0, 0.3)";
              }}
            >
              🚀 AI Bike Finder
            </Link>
            <Link
              to="/calculator/emi"
              style={{
                padding: "12px 25px",
                background: "transparent",
                color: "#ff7a00",
                borderRadius: "25px",
                textDecoration: "none",
                fontWeight: "bold",
                border: "2px solid #ff7a00",
                transition: "all 0.3s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#ff7a00";
                e.target.style.color = "#fff";
                e.target.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "#ff7a00";
                e.target.style.transform = "translateY(0)";
              }}
            >
              💰 EMI Calculator
            </Link>
          </div>
        </div>
      </div>

      {/* Welcome Section */}
      <div style={{ padding: "60px 20px", textAlign: "center", background: "var(--bg2)", color: "var(--text)" }}>
        <h2>Welcome to MotoHub India</h2>
        <p style={{ maxWidth: "800px", margin: "0 auto", fontSize: "1.1rem", lineHeight: 1.6 }}>
          MotoHub India is India's leading platform for motorcycle enthusiasts. Whether you're planning your first bike purchase,
          calculating loan EMIs, tracking fuel expenses, or planning epic road trips, we've got you covered with powerful tools
          and comprehensive resources.
        </p>
      </div>
{/* SEO Content Section */}
<div style={{ padding: "50px 20px", maxWidth: "1100px", margin: "0 auto", color: "var(--text)" }}>
  <h2 style={{ marginBottom: "15px" }}>
    Bike Calculators & Tools for Riders in India
  </h2>

  <p style={{ color: "var(--text2)", lineHeight: 1.8, marginBottom: "15px" }}>
    MotoHub India is designed to help motorcycle riders across India make smarter decisions before buying or riding a bike. 
    Our platform offers powerful tools like EMI calculators, fuel cost estimators, trip planners, and maintenance cost calculators. 
    Whether you are planning to purchase a new bike or manage your riding expenses, our tools provide accurate and real-time insights.
  </p>

  <p style={{ color: "var(--text2)", lineHeight: 1.8, marginBottom: "15px" }}>
    With MotoHub India, you can compare bikes, calculate on-road prices, estimate resale value, and plan long trips efficiently. 
    Our platform is built specifically for Indian riders, keeping local fuel prices, loan rates, and riding conditions in mind. 
    This ensures that all calculations and recommendations are relevant and practical.
  </p>

  <p style={{ color: "var(--text2)", lineHeight: 1.8 }}>
    In addition to calculators, MotoHub India also offers a growing biker community where users can discover routes, join rides, 
    and connect with fellow riders. Whether you are a beginner or an experienced rider, MotoHub India provides everything you need 
    to plan, calculate, and enjoy your biking journey. Top Features "bike tools India",
"motorcycle EMI calculator",
"bike expense calculator India"
  </p>
</div>
      {user && (
        <div style={{ padding: "60px 20px", background: "linear-gradient(180deg, rgba(232,76,30,0.08), rgba(13,13,13,0))", color: "var(--text)" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(280px, 1.1fr) minmax(320px, 1.4fr)", gap: "20px", marginBottom: "22px" }}>
              <div style={dashboardCardStyle}>
                <div style={{ fontSize: "12px", color: "var(--brand)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: "10px" }}>
                  Personalized Dashboard
                </div>
                <h2 style={{ marginBottom: "8px" }}>Welcome back, {firstName}</h2>
                <p style={{ color: "var(--text2)", lineHeight: 1.7, marginBottom: "18px" }}>
                  Your premium rider features are unlocked. Jump back into rides, community chat, and emergency support in one tap.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(120px, 1fr))", gap: "12px" }}>
                  <div style={miniStatStyle}>
                    <div style={miniStatValueStyle}>{user.city || "India"}</div>
                    <div style={miniStatLabelStyle}>Your city</div>
                  </div>
                  <div style={miniStatStyle}>
                    <div style={miniStatValueStyle}>{profileCompletion}%</div>
                    <div style={miniStatLabelStyle}>Profile complete</div>
                  </div>
                  <div style={miniStatStyle}>
                    <div style={miniStatValueStyle}>{recommendedRides.length}</div>
                    <div style={miniStatLabelStyle}>Ride picks</div>
                  </div>
                  <div style={miniStatStyle}>
                    <div style={miniStatValueStyle}>{user.bike || "Add bike"}</div>
                    <div style={miniStatLabelStyle}>Current bike</div>
                  </div>
                </div>
              </div>

              <div style={dashboardCardStyle}>
                <div style={{ fontSize: "12px", color: "var(--brand)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: "10px" }}>
                  Premium Unlocked
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px" }}>
                  {unlockedFeatures.map((feature) => (
                    <Link key={feature.title} to={feature.path} style={dashboardLinkCardStyle}>
                      <div style={{ fontSize: "16px", fontWeight: 700, marginBottom: "6px" }}>{feature.title}</div>
                      <div style={{ fontSize: "13px", color: "var(--text2)", lineHeight: 1.6 }}>{feature.desc}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(280px, 1.3fr) minmax(280px, 1fr)", gap: "20px" }}>
              <div style={dashboardCardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: "12px", color: "var(--brand)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: "6px" }}>
                      Recommended Rides
                    </div>
                    <h3 style={{ margin: 0 }}>Best rides for {user.city || "you"}</h3>
                  </div>
                  <Link to="/rides" style={smallActionStyle}>Open Rides</Link>
                </div>
                <div style={{ display: "grid", gap: "12px" }}>
                  {recommendedRides.map((ride) => (
                    <div key={ride.id} style={rideCardStyle}>
                      <div style={{ fontSize: "16px", fontWeight: 700, marginBottom: "6px" }}>{ride.title}</div>
                      <div style={{ fontSize: "13px", color: "var(--text2)", marginBottom: "8px" }}>
                        {ride.from} to {ride.to} · {ride.date}
                      </div>
                      <div style={{ fontSize: "13px", color: "var(--text3)" }}>
                        {ride.distance} · {ride.joined}/{ride.max} riders · {ride.bikeReq}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={dashboardCardStyle}>
                <div style={{ fontSize: "12px", color: "var(--brand)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: "10px" }}>
                  Quick Resume
                </div>
                <h3 style={{ marginBottom: "14px" }}>Continue with your tools</h3>
                <div style={{ display: "grid", gap: "12px" }}>
                  {recentTools.map((tool) => (
                    <Link key={tool.title} to={tool.path} style={toolLinkStyle}>
                      <div style={{ fontSize: "15px", fontWeight: 700, marginBottom: "6px" }}>{tool.title}</div>
                      <div style={{ fontSize: "13px", color: "var(--text2)", lineHeight: 1.6 }}>{tool.desc}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Cards */}
      <div style={{ padding: "40px 20px 20px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <h2 style={{ textAlign: "center", marginBottom: "28px", color: "var(--text)" }}>
            Discover Community Features
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px" }}>
            {communityHighlights.map((item) => (
              <div key={item.title} style={communityCardStyle}>
                <h3 style={{ margin: "0 0 8px", color: "var(--text)" }}>{item.title}</h3>
                <p style={{ margin: 0, color: "var(--text2)", lineHeight: 1.7 }}>{item.body}</p>
                <Link to={item.ctaPath} style={communityCtaStyle}>
                  {item.ctaLabel}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: "60px 20px" }}>
        <h2 style={{ textAlign: "center", marginBottom: "40px", color: "var(--text)" }}>Explore Our Features</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px", maxWidth: "1200px", margin: "0 auto" }}>
          <Link to="/calculator/emi" style={cardStyle} className="card-hover">
            <h3>💰 EMI Calculator</h3>
            <p>Calculate bike loan EMIs instantly with accurate interest rates and tenure options.</p>
          </Link>
          <Link to="/calculator/fuel" style={cardStyle} className="card-hover">
            <h3>⛽ Fuel Cost Tracker</h3>
            <p>Track and optimize your fuel expenses with detailed mileage and cost analysis.</p>
          </Link>
          <Link to="/calculator/trip" style={cardStyle} className="card-hover">
            <h3>📖 Trip Planner</h3>
            <p>Plan your motorcycle adventures with route optimization and cost estimation.</p>
          </Link>
          <Link to="/compare" style={cardStyle} className="card-hover">
            <h3>⚡ Bike Comparison</h3>
            <p>Compare bikes side-by-side with detailed specs, prices, and user reviews.</p>
          </Link>
          <Link to="/bikes" style={cardStyle} className="card-hover">
            <h3>🏍️ Bike Database</h3>
            <p>Explore our comprehensive database of bikes available in India.</p>
          </Link>
          <Link to="/routes" style={cardStyle} className="card-hover">
            <h3>🗺️ Riding Routes</h3>
            <p>Discover curated riding routes across India for every skill level.</p>
          </Link>
        </div>
      </div>

      <div style={{ padding: "60px 20px", background: "var(--bg2)", color: "var(--text)" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px", flexWrap: "wrap", marginBottom: "30px" }}>
            <div>
              <h2 style={{ marginBottom: "8px" }}>Popular Riding Products</h2>
              <p style={{ color: "var(--text2)", maxWidth: "620px" }}>
                Explore a few handpicked products from the MotoHub India shop and open the full shopping page to browse more gear.
              </p>
            </div>
            <Link to="/shop" style={shopLinkStyle}>
              View Full Shop
            </Link>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
            {featuredProducts.map((product) => (
              <Link key={product.id} to="/shop" style={productCardStyle}>
                <img
                  src={product.image}
                  alt={product.name}
                  style={{
                    width: "100%",
                    height: "180px",
                    objectFit: "cover",
                    borderRadius: "14px",
                    marginBottom: "14px",
                    background: "var(--bg3)",
                  }}
                />
                <div style={{ fontSize: "12px", color: "var(--brand)", textTransform: "uppercase", letterSpacing: 1, marginBottom: "8px" }}>
                  {product.category}
                </div>
                <h3 style={{ marginBottom: "8px", color: "var(--text)" }}>{product.name}</h3>
                <p style={{ color: "var(--text2)", marginBottom: "14px" }}>{product.price}</p>
                <span style={productButtonStyle}>Check In Shop</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Login Encouragement Section */}
      {!user && (
      <div style={{ padding: "60px 20px", background: "var(--bg3)", color: "var(--text)" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}>
          <h2>Unlock Premium Features with Login</h2>
          <p style={{ fontSize: "1.1rem", marginBottom: "30px" }}>
            Create a free account to access personalized features and enhance your riding experience.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px", marginBottom: "40px" }}>
            <div style={unlockStyle}>
              <h4>💾 Save Calculations</h4>
              <p>Store and revisit your EMI calculations, trip plans, and comparisons.</p>
            </div>
            <div style={unlockStyle}>
              <h4>📊 Personalized Insights</h4>
              <p>Get tailored recommendations based on your riding preferences.</p>
            </div>
            <div style={unlockStyle}>
              <h4>🔔 Notifications</h4>
              <p>Receive updates on new bike launches and route recommendations.</p>
            </div>
            <div style={unlockStyle}>
              <h4>👥 Community Access</h4>
              <p>Join discussions, share experiences, and connect with fellow riders.</p>
            </div>
          </div>
          <Link to="/login" style={ctaStyle} className="cta-button">Login / Sign Up Now</Link>
        </div>
      </div>
      )}

      {/* About Section */}
      <div style={{ padding: "60px 20px", color: "var(--text)" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <h2 style={{ textAlign: "center", marginBottom: "40px" }}>Why Choose MotoHub India?</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "30px" }}>
            <div>
              <h3>🔧 Comprehensive Calculators</h3>
              <p>Accurate EMI, fuel, maintenance, and resale value calculators to help you make informed decisions.</p>
            </div>
            <div>
              <h3>🛒 Affiliate Shop</h3>
              <p>Access quality riding gear and accessories through our trusted affiliate partners.</p>
            </div>
            <div>
              <h3>🚨 SOS Emergency Support</h3>
              <p>24/7 emergency assistance for riders in need, with location tracking and roadside help.</p>
            </div>
            <div>
              <h3>📚 Rich Blog Content</h3>
              <p>Stay updated with the latest bike news, reviews, maintenance tips, and riding guides.</p>
            </div>
            <div>
              <h3>🎯 User-Friendly Interface</h3>
              <p>Intuitive design that works seamlessly on desktop and mobile devices.</p>
            </div>
            <div>
              <h3>🇮🇳 Made for India</h3>
              <p>Tailored for Indian riders with local pricing, routes, and market insights.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div style={{ padding: "60px 20px", background: "var(--brand)", color: "#fff", textAlign: "center" }}>
        <h2>Join Thousands of Happy Riders</h2>
        <div style={{ display: "flex", justifyContent: "center", gap: "40px", flexWrap: "wrap", marginTop: "30px" }}>
          <div>
            <h3 style={{ fontSize: "2rem", margin: 0 }}>10K+</h3>
            <p>Calculations Made</p>
          </div>
          <div>
            <h3 style={{ fontSize: "2rem", margin: 0 }}>5K+</h3>
            <p>Routes Planned</p>
          </div>
          <div>
            <h3 style={{ fontSize: "2rem", margin: 0 }}>2K+</h3>
            <p>Bikes Compared</p>
          </div>
          <div>
            <h3 style={{ fontSize: "2rem", margin: 0 }}>1K+</h3>
            <p>Happy Users</p>
          </div>
        </div>
      </div>
    </>
  );
}

const cardStyle = {
  padding: "30px 20px",
  background: "rgba(255,255,255,0.05)",
  backdropFilter: "blur(15px)",
  borderRadius: "20px",
  textAlign: "center",
  textDecoration: "none",
  color: "var(--text)",
  cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.1)",
  display: "block",
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
  transform: "perspective(1000px)",
};

const unlockStyle = {
  padding: "20px",
  background: "var(--bg2)",
  borderRadius: "var(--radius)",
  textAlign: "center",
};

const ctaStyle = {
  display: "inline-block",
  padding: "15px 30px",
  background: "var(--brand)",
  color: "#fff",
  textDecoration: "none",
  borderRadius: "var(--radius)",
  fontWeight: "bold",
  fontSize: "1.1rem",
  transition: "background 0.3s",
};

const shopLinkStyle = {
  display: "inline-block",
  padding: "12px 22px",
  background: "var(--brand)",
  color: "#fff",
  textDecoration: "none",
  borderRadius: "999px",
  fontWeight: "bold",
};

const productCardStyle = {
  display: "block",
  background: "var(--bg3)",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  padding: "16px",
  textDecoration: "none",
  color: "var(--text)",
  boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
  transition: "transform 0.2s ease, border-color 0.2s ease",
};

const productButtonStyle = {
  display: "inline-block",
  padding: "10px 16px",
  borderRadius: "999px",
  background: "rgba(232, 76, 30, 0.12)",
  color: "var(--brand)",
  fontWeight: "bold",
};

const dashboardCardStyle = {
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "20px",
  padding: "22px",
  boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
};

const miniStatStyle = {
  background: "var(--bg3)",
  border: "1px solid var(--border)",
  borderRadius: "14px",
  padding: "14px",
};

const miniStatValueStyle = {
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text)",
  marginBottom: "6px",
  wordBreak: "break-word",
};

const miniStatLabelStyle = {
  fontSize: "12px",
  color: "var(--text3)",
  textTransform: "uppercase",
  letterSpacing: 1,
};

const dashboardLinkCardStyle = {
  display: "block",
  textDecoration: "none",
  color: "var(--text)",
  background: "var(--bg3)",
  border: "1px solid var(--border)",
  borderRadius: "16px",
  padding: "16px",
};

const rideCardStyle = {
  background: "var(--bg3)",
  border: "1px solid var(--border)",
  borderRadius: "16px",
  padding: "16px",
};

const toolLinkStyle = {
  display: "block",
  textDecoration: "none",
  color: "var(--text)",
  background: "var(--bg3)",
  border: "1px solid var(--border)",
  borderRadius: "16px",
  padding: "16px",
};

const smallActionStyle = {
  display: "inline-block",
  padding: "10px 16px",
  borderRadius: "999px",
  background: "rgba(232,76,30,0.14)",
  color: "var(--brand)",
  textDecoration: "none",
  fontWeight: 700,
};

const communityCardStyle = {
  background: "var(--bg3)",
  border: "1px solid var(--border)",
  borderRadius: "16px",
  padding: "18px",
  boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
};

const communityCtaStyle = {
  display: "inline-block",
  marginTop: "14px",
  padding: "9px 14px",
  borderRadius: "999px",
  background: "rgba(232,76,30,0.14)",
  color: "var(--brand)",
  textDecoration: "none",
  fontWeight: 700,
  fontSize: "13px",
};
